import React from 'react';
import {
  Row,
  Col,
  DatePicker,
  Typography,
  Skeleton,
  Result,
  Form,
  Divider,
  Select,
  Input,
  Button,
  Statistic,
  Table,
  Tag,
  Space
} from 'antd'
import {
  SearchOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { convertPaymentType, formatPhone } from '../../utils/customFunctions';
import { paymentTypes, transactionStatus } from '../../utils/masterData';
import { useQuery } from '@tanstack/react-query';
import ChangeOrderStatus from './components/ChangeOrderStatus';

const columns = [
  {
    title: 'วันที่',
    dataIndex: 'order_date',
    key: 'order_date',
    render: (text, record) => <Button
      type='text'
      href={`/admin/orders/${record.order_id}`}
      style={{
        background: 'none',
        textAlign: 'left'
      }}>
      {dayjs(text).format('DD-MM-YYYY')}
    </Button>
  },
  {
    title: 'เลขที่คำสั่งซื้อ',
    dataIndex: 'doc_no',
    key: 'doc_no',
    render: (text, record) => <Button
      type='text'
      href={`/admin/orders/${record.order_id}`}
      style={{
        background: 'none',
        textAlign: 'left'
      }}>
      {text}
    </Button>
  },
  {
    title: 'ลูกหนี้',
    dataIndex: 'customer',
    key: 'customer_name',
    render: (text, record) => <Button
      type='text'
      href={`/admin/orders/${record.order_id}`}
      style={{
        background: 'none',
        textAlign: 'left'
      }}>
      {text.fname}
    </Button>
  },
  {
    title: 'เบอร์โทรศัพท์',
    dataIndex: 'customer',
    key: 'customer_phone',
    render: (text, record) => <Button
      type='text'
      href={`/admin/orders/${record.order_id}`}
      style={{
        background: 'none',
        textAlign: 'left'
      }}>
      {formatPhone(text.phone)}{text.phone2 ? ` ; ${formatPhone(text.phone2)}` : ''}
    </Button>
  },
  {
    title: 'สินค้า',
    dataIndex: 'product',
    key: 'product',
    render: (text, record) => (
      <>
        <Button
          type='text'
          href={`/admin/orders/${record.order_id}`}
          style={{
            background: 'none',
            textAlign: 'left'
          }}>{text.description ?? text.name}
        </Button>
        <Button
          type='text'
          href={`/admin/orders/${record.order_id}`}
          style={{
            background: 'none',
            textAlign: 'left'
          }}>
          <Tag color="purple">{text.category?.name}</Tag>
          <Tag color="magenta">{text.brand?.name}</Tag>
        </Button>
      </>
    )
  },
  {
    title: 'ราคา',
    dataIndex: 'price',
    key: 'price',
    render: (text, record) =>
      <Button
        type='text'
        href={`/admin/orders/${record.order_id}`}
        style={{
          background: 'none',
          textAlign: 'left'
        }}>
        <Statistic value={text} precision={2} valueStyle={{ fontSize: 14, textAlign: 'right' }} />
      </Button>
  },
  {
    title: 'ประเภทการขาย',
    dataIndex: 'payment_type',
    key: 'payment_type',
    render: (text, record) =>
      <Button
        type='text'
        href={`/admin/orders/${record.order_id}`}
        style={{
          background: 'none',
          textAlign: 'left'
        }}>
        {convertPaymentType(text)}
      </Button>
  },
  {
    title: 'สถานะ',
    dataIndex: 'transaction_status',
    key: 'transaction_status',
    width: '10%',
    render: (text, record) => {
      switch (text) {
        case 'COMPLETED': return <Tag color="success">สำเร็จ</Tag>
        case 'CANCLED': return <Tag color="error">ยกเลิก</Tag>
        default: return (
          <Space>
            <Tag color="processing">อยู่ระหว่างดำเนินงาน</Tag>
            <ChangeOrderStatus order={record} />
          </Space>
        );
      }
    }
  },
]

// Helper function สำหรับสร้าง query string ที่ถูกต้อง
const buildSearchUrl = (values) => {
  const params = new URLSearchParams();

  if (values.search_info?.trim()) {
    params.append('search_info', values.search_info.trim());
  }
  if (values.payment_type) {
    params.append('payment_type', values.payment_type);
  }
  if (values.transaction_status) {
    params.append('transaction_status', values.transaction_status);
  }
  if (values.date && values.date[0] && values.date[1]) {
    params.append('start_date', dayjs(values.date[0]).toISOString());
    params.append('end_date', dayjs(values.date[1]).toISOString());
  }

  const queryString = params.toString();
  // ถ้าไม่มี filter ให้กลับไปหน้า orders
  return queryString ? `/admin/searchOrder?${queryString}` : '/admin/orders';
};

function SearchOrder() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  // ดึง params จาก URL
  const searchParams = new URLSearchParams(location.search);
  const search_info = searchParams.get('search_info');
  const transaction_status = searchParams.get('transaction_status');
  const payment_type = searchParams.get('payment_type');
  const start_date = searchParams.get('start_date');
  const end_date = searchParams.get('end_date');

  // สร้าง API URL - เก็บแบบเดิมที่ใช้ window.location.href.split("?")[1]
  const queryPart = window.location.href.split("?")[1];
  const apiText = `/api/transaction/getOrderItems${queryPart ? `?${queryPart}` : ''}`;

  // ตรวจสอบว่ามี filter อยู่หรือไม่
  const hasFilters = search_info || transaction_status || payment_type || start_date || end_date;

  const { isError, isLoading, data, error } = useQuery({
    queryKey: [apiText],
  });

  const onSearch = () => {
    form
      .validateFields()
      .then(values => {
        const url = buildSearchUrl(values);
        navigate(url);
      })
      .catch(err => {
        console.log(err);
      });
  };

  // ฟังก์ชัน Clear Filter - กลับไปหน้า /admin/orders
  const onClearFilter = () => {
    form.resetFields();
    navigate('/admin/orders');
  };

  if (isLoading) {
    return <Skeleton active />;
  }

  if (isError) {
    return <Result status="error" title={error.message} />;
  }

  return (
    <div style={{ background: '#fff' }}>
      <Row>
        <Col xs={24}>
          <Row justify='space-between'>
            <Col>
              <Typography.Title level={3} style={{ padding: '0px', margin: '0px' }}>
                ค้นหาการขายสินค้า
              </Typography.Title>
            </Col>
          </Row>
        </Col>
        <Divider />
        <Col xs={24}>
          <Row justify='space-between' gutter={[0, 16]}>
            <Col xs={24}>
              <Form
                form={form}
                name="searchOrder"
                layout="vertical"
                initialValues={{
                  search_info: search_info ?? undefined,
                  transaction_status: transaction_status ?? undefined,
                  payment_type: payment_type ?? undefined,
                  ...((start_date && end_date) && { date: [dayjs(start_date), dayjs(end_date)] })
                }}
              >
                <Row gutter={[8, 0]}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="date" label="วันที่">
                      <DatePicker.RangePicker
                        placeholder={["วันเริ่มต้น", "วันสิ้นสุด"]}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="ประเภทการขาย"
                      name="payment_type"
                    >
                      <Select
                        placeholder="เลือกประเภทการขาย"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {paymentTypes.map(payment => (
                          <Select.Option key={payment.value} value={payment.value}>
                            {payment.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label="สถานะ"
                      name="transaction_status"
                    >
                      <Select
                        placeholder="เลือกสถานะ"
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {transactionStatus.map(transaction => (
                          <Select.Option key={transaction.value} value={transaction.value}>
                            {transaction.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Row gutter={[8, 0]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label=""
                          name="search_info"
                        >
                          <Input
                            placeholder="ค้นหา เลขที่คำสั่งซื้อ ลูกหนี้ หมายเลขโทรศัพท์"
                            onPressEnter={onSearch}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={6}>
                        <Space>
                          <Button
                            type='primary'
                            icon={<SearchOutlined />}
                            onClick={onSearch}
                          >
                            ค้นหา
                          </Button>
                          {hasFilters && (
                            <Button
                              icon={<ClearOutlined />}
                              onClick={onClearFilter}
                            >
                              ล้างตัวกรอง
                            </Button>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            </Col>
            <Col xs={24}>
              <Table
                rowKey={(record) => record.item_id}
                columns={columns}
                dataSource={data}
                scroll={{ x: 1200 }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
}

export default SearchOrder;